import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useEvent } from '../../hooks/useEvents';
import {
  formatDateLong,
  getCountdown,
  toGoogleCalendar,
} from '../../utils/attendeeUtils';
import {
  toGoogleMapsDirections,
  toGoogleMapsPlace,
  toFlights,
  toAirbnb,
  toExpediaHotels,
  toUber,
  toLyft,
  toParking,
} from '../../utils/helpers';
import { isNativePlatform } from '../../native/platform';
import { shareContent } from '../../native/share';
import EventTicket from '../../components/EventTicket';
import LoadingState from '../../components/attendee/LoadingState';
import EmptyState from '../../components/attendee/EmptyState';

const EventHub = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { event, loading, error } = useEvent(eventId);
  const [registration, setRegistration] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [showTicket, setShowTicket] = useState(false);

  useEffect(() => {
    const fetchRegistration = async () => {
      if (!event?.id || !user?.email) return;
      try {
        const { data } = await supabase
          .from('event_registrations')
          .select('*')
          .eq('event_id', event.id)
          .eq('attendee_email', user.email.toLowerCase())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        setRegistration(data);
      } catch (err) {
        console.error('Error fetching registration:', err);
      }
    };
    fetchRegistration();
  }, [event?.id, user?.email]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingState variant="hero" />
        <LoadingState count={3} />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          title="Event not found"
          message="This event may have been removed or is no longer available."
          ctaText="Back to My Events"
          ctaLink="/attendee/events"
        />
      </div>
    );
  }

  const venue = event.venue_name || event.venue;
  const city = event.city || event.location;
  const address = event.address || [venue, city].filter(Boolean).join(', ');
  const countdown = getCountdown(event.start_date || event.start);
  const isCancelled = registration?.status === 'cancelled';
  const isPaid = registration?.payment_status === 'paid';
  const speakers = event.speakers || [];

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/events/${event.id}`;
    if (isNativePlatform()) {
      await shareContent({
        title: event.title || event.event,
        text: `Join me at ${event.title || event.event}!`,
        url: shareUrl,
        dialogTitle: 'Share Event',
      });
    } else if (navigator.share) {
      try {
        await navigator.share({ title: event.title || event.event, url: shareUrl });
      } catch (_) {}
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Event link copied to clipboard!');
    }
  };

  const sections = [
    { key: 'overview', label: 'Overview', icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { key: 'speakers', label: 'Speakers', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
    { key: 'ticket', label: 'My Ticket', icon: 'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z' },
    { key: 'venue', label: 'Venue', icon: 'M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z' },
    { key: 'travel', label: 'Travel', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back link */}
      <button
        onClick={() => navigate('/attendee/events')}
        className="flex items-center text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to My Events
      </button>

      {/* Event Banner */}
      <div className="relative rounded-2xl overflow-hidden shadow-xl mb-6">
        {event.image_url ? (
          <div className="relative h-48 sm:h-64">
            <img src={event.image_url} alt={event.title || event.event} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          </div>
        ) : (
          <div className="h-48 sm:h-64 bg-gradient-to-br from-indigo-600 to-purple-700" />
        )}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            {event.title || event.event}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-white/90">
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formatDateLong(event.start_date || event.start)}
            </span>
            {venue && (
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                {venue}
              </span>
            )}
            {countdown && (
              <span className="px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold">
                {countdown} to go
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Registration status */}
      {registration && (
        <div className="flex items-center gap-2 mb-6">
          {isCancelled ? (
            <span className="px-3 py-1 bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 rounded-full text-sm font-medium">
              Registration Cancelled
            </span>
          ) : (
            <>
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded-full text-sm font-medium">
                Registered
              </span>
              {isPaid ? (
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium">
                  Paid
                </span>
              ) : (
                <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 rounded-full text-sm font-medium">
                  Payment Pending
                </span>
              )}
              {registration.checked_in_at && (
                <span className="px-3 py-1 bg-teal-100 dark:bg-teal-900/50 text-teal-800 dark:text-teal-300 rounded-full text-sm font-medium">
                  Checked In
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {registration && !isCancelled && (
          <button
            onClick={() => setShowTicket(true)}
            className="flex flex-col items-center gap-1.5 p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">My Ticket</span>
          </button>
        )}
        <Link
          to="/schedule"
          className="flex flex-col items-center gap-1.5 p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Schedule</span>
        </Link>
        <button
          onClick={() => setActiveSection('speakers')}
          className="flex flex-col items-center gap-1.5 p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Speakers</span>
        </button>
        {address && (
          <a
            href={toGoogleMapsDirections(address)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-1.5 p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Directions</span>
          </a>
        )}
        <button
          onClick={() => window.open(toGoogleCalendar(event), '_blank')}
          className="flex flex-col items-center gap-1.5 p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Calendar</span>
        </button>
        <button
          onClick={handleShare}
          className="flex flex-col items-center gap-1.5 p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367-2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Share</span>
        </button>
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {sections.map(section => (
          <button
            key={section.key}
            onClick={() => setActiveSection(section.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeSection === section.key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={section.icon} />
            </svg>
            {section.label}
          </button>
        ))}
      </div>

      {/* Section content */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          {/* Event details */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Event Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dates</p>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {formatDateLong(event.start_date || event.start)}
                </p>
                {event.end_date && event.end_date !== event.start_date && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    through {formatDateLong(event.end_date || event.end)}
                  </p>
                )}
              </div>
              {venue && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Venue</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">{venue}</p>
                </div>
              )}
              {city && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">{city}</p>
                </div>
              )}
              {event.org && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organization</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">{event.org}</p>
                </div>
              )}
              {event.event_type && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1 capitalize">{event.event_type.replace('_', ' ')}</p>
                </div>
              )}
            </div>
            {event.description && (
              <div className="mt-6">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">About</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{event.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeSection === 'speakers' && (
        <div>
          {speakers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {speakers.map(speaker => (
                <Link
                  key={speaker.id}
                  to={`/speakers/${speaker.id}`}
                  className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
                >
                  <img
                    src={speaker.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(speaker.name)}&background=7c3aed&color=fff&size=64`}
                    alt={speaker.name}
                    className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate">{speaker.name}</h3>
                    {speaker.title && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{speaker.title}</p>}
                    {speaker.session_title && (
                      <p className="text-xs text-primary mt-1 truncate">{speaker.session_title}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No speakers announced"
              message="Speaker information for this event hasn't been published yet."
            />
          )}
        </div>
      )}

      {activeSection === 'ticket' && (
        <div>
          {registration && !isCancelled ? (
            <div className="max-w-md mx-auto">
              <EventTicket registration={registration} event={event} showDownload={true} />
              {registration.checked_in_at && (
                <div className="mt-4 p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl text-center">
                  <p className="text-sm font-medium text-teal-800 dark:text-teal-300">
                    ✓ Checked in at {new Date(registration.checked_in_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <EmptyState
              title="No ticket available"
              message={isCancelled ? "Your registration was cancelled." : "You're not registered for this event."}
              ctaText={isCancelled ? "Browse Events" : "Register Now"}
              ctaLink={isCancelled ? "/events" : `/register?event=${event.id}`}
            />
          )}
        </div>
      )}

      {activeSection === 'venue' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Venue Information</h2>
            {venue && <p className="text-sm font-medium text-gray-900 dark:text-white">{venue}</p>}
            {address && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{address}</p>}
            {address && (
              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={toGoogleMapsPlace(address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  View on Map
                </a>
                <a
                  href={toGoogleMapsDirections(address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Directions
                </a>
                {event.latitude && event.longitude && (
                  <>
                    <a
                      href={toUber(address, event.latitude, event.longitude)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Uber
                    </a>
                    <a
                      href={toLyft(address, event.latitude, event.longitude)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      Lyft
                    </a>
                  </>
                )}
                <a
                  href={toParking(address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Parking
                </a>
              </div>
            )}
          </div>
          {address && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
              <iframe
                title="Venue map"
                width="100%"
                height="300"
                loading="lazy"
                src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`}
                className="border-0"
              />
            </div>
          )}
        </div>
      )}

      {activeSection === 'travel' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Travel & Accommodation</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {event.airport || event.nearest_airport ? (
                <a
                  href={toFlights({ to: city || address, start: event.start_date || event.start, end: event.end_date || event.end })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Flights</span>
                </a>
              ) : null}
              <a
                href={toExpediaHotels(city || address, event.start_date || event.start, event.end_date || event.end)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Hotels</span>
              </a>
              <a
                href={toAirbnb(city || address, event.start_date || event.start, event.end_date || event.end)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Airbnb</span>
              </a>
            </div>
            {event.hotels && (
              <div className="mt-6">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Recommended Hotels</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{event.hotels}</p>
              </div>
            )}
          </div>
          <Link
            to="/travel"
            className="block p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-primary">View full travel information</span>
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        </div>
      )}

      {/* Ticket Modal */}
      {showTicket && registration && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowTicket(false)}
        >
          <div className="max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setShowTicket(false)}
                className="text-white/80 hover:text-white"
                aria-label="Close ticket"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <EventTicket registration={registration} event={event} showDownload={true} />
          </div>
        </div>
      )}
    </div>
  );
};

export default EventHub;
